/**
 * Material Design 3 Color System
 * Tonal palettes для light и dark mode
 */

// Primary - фиолетовый (основной акцент)
export const primary = {
  0: '#000000',
  10: '#21005D',
  20: '#381E72',
  30: '#4F378B',
  40: '#6750A4',
  50: '#7F67BE',
  60: '#9A82DB',
  70: '#B69DF8',
  80: '#D0BCFF',
  90: '#EADDFF',
  95: '#F6EDFF',
  99: '#FFFBFE',
  100: '#FFFFFF',
};

// Secondary - мягкий фиолетовый
export const secondary = {
  0: '#000000',
  10: '#1D192B',
  20: '#332D41',
  30: '#4A4458',
  40: '#625B71',
  50: '#7A7289',
  60: '#958DA5',
  70: '#B0A7C0',
  80: '#CCC2DC',
  90: '#E8DEF8',
  95: '#F6EDFF',
  99: '#FFFBFE',
  100: '#FFFFFF',
};

// Tertiary - бирюзовый (дополнительный акцент)
export const tertiary = {
  0: '#000000',
  10: '#001F24',
  20: '#00363D',
  30: '#004F58',
  40: '#006874',
  50: '#008391',
  60: '#00A0B0',
  70: '#00BCD4',
  80: '#4DD0E1',
  90: '#B2EBF2',
  95: '#D7F8FC',
  99: '#F5FDFE',
  100: '#FFFFFF',
};

// Error - красный
export const error = {
  0: '#000000',
  10: '#410E0B',
  20: '#601410',
  30: '#8C1D18',
  40: '#B3261E',
  50: '#DC362E',
  60: '#E46962',
  70: '#EC928E',
  80: '#F2B8B5',
  90: '#F9DEDC',
  95: '#FCEEEE',
  99: '#FFFBF9',
  100: '#FFFFFF',
};

// Success - зелёный
export const success = {
  0: '#000000',
  10: '#002106',
  20: '#00390B',
  30: '#005313',
  40: '#006E1C',
  50: '#008A25',
  60: '#1EA446',
  70: '#4CC068',
  80: '#6DDC89',
  90: '#90F9A4',
  95: '#C7FFD0',
  99: '#F5FFF5',
  100: '#FFFFFF',
};

// Warning - оранжевый
export const warning = {
  0: '#000000',
  10: '#311300',
  20: '#4D2600',
  30: '#6B3A00',
  40: '#8B4F00',
  50: '#AC6500',
  60: '#CF7C00',
  70: '#F39400',
  80: '#FFB945',
  90: '#FFDDB3',
  95: '#FFEEDD',
  99: '#FFFBFF',
  100: '#FFFFFF',
};

// Neutral - серые тона для фонов и текста
export const neutral = {
  0: '#000000',
  4: '#0D0D0F',
  6: '#131316',
  10: '#1C1B1F',
  12: '#201F23',
  17: '#2A292D',
  20: '#313033',
  22: '#353438',
  24: '#39383C',
  30: '#484649',
  40: '#605D62',
  50: '#79767D',
  60: '#938F96',
  70: '#AEA9B1',
  80: '#CAC4D0',
  87: '#DDD8E0',
  90: '#E6E1E5',
  92: '#ECE6F0',
  94: '#F3EDF7',
  95: '#F5EFF7',
  96: '#F7F2FA',
  98: '#FEF7FF',
  99: '#FFFBFE',
  100: '#FFFFFF',
};

// Neutral Variant - для границ и outline
export const neutralVariant = {
  0: '#000000',
  10: '#1D1A22',
  20: '#322F37',
  30: '#49454F',
  40: '#605D66',
  50: '#79747E',
  60: '#938F99',
  70: '#AEA9B4',
  80: '#CAC4D0',
  90: '#E7E0EC',
  95: '#F5EEFA',
  99: '#FFFBFE',
  100: '#FFFFFF',
};

/**
 * Light Theme Scheme
 */
export const lightScheme = {
  primary: primary[40],
  onPrimary: primary[100],
  primaryContainer: primary[90],
  onPrimaryContainer: primary[10],
  
  secondary: secondary[40],
  onSecondary: secondary[100],
  secondaryContainer: secondary[90],
  onSecondaryContainer: secondary[10],
  
  tertiary: tertiary[40],
  onTertiary: tertiary[100],
  tertiaryContainer: tertiary[90],
  onTertiaryContainer: tertiary[10],
  
  error: error[40],
  onError: error[100],
  errorContainer: error[90],
  onErrorContainer: error[10],
  
  success: success[40],
  onSuccess: success[100],
  successContainer: success[90],
  onSuccessContainer: success[10],
  
  warning: warning[40],
  onWarning: warning[100],
  warningContainer: warning[90],
  onWarningContainer: warning[10],
  
  background: neutral[99],
  onBackground: neutral[10],
  
  surface: neutral[99],
  onSurface: neutral[10],
  surfaceVariant: neutralVariant[90],
  onSurfaceVariant: neutralVariant[30],
  
  surfaceContainerLowest: neutral[100],
  surfaceContainerLow: neutral[96],
  surfaceContainer: neutral[94],
  surfaceContainerHigh: neutral[92],
  surfaceContainerHighest: neutral[90],
  
  outline: neutralVariant[50],
  outlineVariant: neutralVariant[80],
  
  inverseSurface: neutral[20],
  inverseOnSurface: neutral[95],
  inversePrimary: primary[80],
  
  shadow: neutral[0],
  scrim: neutral[0],
};

/**
 * Dark Theme Scheme
 */
export const darkScheme = {
  primary: primary[80],
  onPrimary: primary[20],
  primaryContainer: primary[30],
  onPrimaryContainer: primary[90],
  
  secondary: secondary[80],
  onSecondary: secondary[20],
  secondaryContainer: secondary[30],
  onSecondaryContainer: secondary[90],
  
  tertiary: tertiary[80],
  onTertiary: tertiary[20],
  tertiaryContainer: tertiary[30],
  onTertiaryContainer: tertiary[90],
  
  error: error[80],
  onError: error[20],
  errorContainer: error[30],
  onErrorContainer: error[90],
  
  success: success[80],
  onSuccess: success[20],
  successContainer: success[30],
  onSuccessContainer: success[90],
  
  warning: warning[80],
  onWarning: warning[20],
  warningContainer: warning[30],
  onWarningContainer: warning[90],
  
  background: neutral[6],
  onBackground: neutral[90],
  
  surface: neutral[6],
  onSurface: neutral[90],
  surfaceVariant: neutralVariant[30],
  onSurfaceVariant: neutralVariant[80],
  
  surfaceContainerLowest: neutral[4],
  surfaceContainerLow: neutral[10],
  surfaceContainer: neutral[12],
  surfaceContainerHigh: neutral[17],
  surfaceContainerHighest: neutral[22],
  
  outline: neutralVariant[60],
  outlineVariant: neutralVariant[30],
  
  inverseSurface: neutral[90],
  inverseOnSurface: neutral[20],
  inversePrimary: primary[40],
  
  shadow: neutral[0],
  scrim: neutral[0],
};
